import { apiClient } from "./client";
import { GeoPrecisione } from "./clients";

/**
 * Parco device FISICO — le singole casseforti, con la loro matricola.
 *
 * Da non confondere con `api/catalogo.ts`, che è il MODELLO (marca/modello/
 * misure/documentazione): lì una riga per "Yougo U-ONE 2000E", qui una riga per
 * ogni pezzo realmente installato da qualche parte.
 */

export type StatoDevice = "attivo" | "dismesso" | "rubato";
export type TipoPossessoDevice = "venduto_evo" | "venduto_terzi" | "noleggio" | "proprieta_cliente";

/** Etichette leggibili: gli enum arrivano in snake_case dal database. */
export const STATO_LABELS: Record<StatoDevice, string> = {
  attivo: "Attivo",
  dismesso: "Dismesso",
  rubato: "Rubato",
};

export type TipoServizioPrecredit =
  | "wink_on_cash"
  | "evo_cash"
  | "bam"
  | "mondialpol"
  | "nessun_collegamento_banca"
  | "fabrik"
  | "altro";

/** Ordine di dichiarazione = ordine con cui compaiono nel riepilogo. */
export const SERVIZI_PRECREDIT: TipoServizioPrecredit[] = [
  "wink_on_cash",
  "evo_cash",
  "bam",
  "mondialpol",
  "fabrik",
  "nessun_collegamento_banca",
  // In fondo di proposito: `altro` è la voce residuale, e una residuale in
  // mezzo all'elenco si legge come un servizio qualsiasi.
  "altro",
];

/** L'etichetta porta la banca partner: è ciò che distingue un servizio dall'altro. */
export const SERVIZIO_PRECREDIT_LABELS: Record<TipoServizioPrecredit, string> = {
  wink_on_cash: "Wink On Cash (Banca Azzoaglio)",
  evo_cash: "Evo Cash (Banca d'Alba)",
  bam: "BAM (Banca Alpi Marittime)",
  mondialpol: "Mondialpol (Banca Intesa)",
  fabrik: "Fabrik (Axerve)",
  nessun_collegamento_banca: "Nessun Collegamento Banca",
  altro: "Altro",
};

/**
 * Colore di ciascun servizio nel riepilogo. I valori vengono dalla palette già
 * usata dai grafici (PALETTE_EXTRA in DonutPerStato): un colore inventato qui
 * avrebbe reso questa pagina l'unica con un linguaggio cromatico suo.
 * `nessun_collegamento_banca` prende il grigio, che nell'app segnala già
 * un'assenza e non un servizio.
 *
 * Con `fabrik` e `altro` la palette è esaurita: sono gli ultimi due colori
 * liberi di PALETTE_EXTRA. Un nono servizio non avrà un colore proprio da
 * prendere, e a quel punto la scelta è ampliare la palette per tutta l'app —
 * non aggiungere un hex solo qui.
 */
export const SERVIZIO_PRECREDIT_COLORE: Record<TipoServizioPrecredit, string> = {
  // Verde ufficiale del marchio, preso dal file vettoriale (`.cls-1` in
  // winkoncash.svg) e non dalla guida di visual identity: sono i colori
  // realmente disegnati nel logo, quindi combaciano con quello che l'utente
  // vede accanto al numero.
  wink_on_cash: "#009f3c",
  evo_cash: "#8b5cf6",
  bam: "#0891b2",
  mondialpol: "#c2410c",
  fabrik: "#be123c",
  nessun_collegamento_banca: "#87908b",
  altro: "#4d7c0f",
};

/** Forma breve, per quando lo spazio è quello di una tessera del riepilogo. */
export const SERVIZIO_PRECREDIT_BREVE: Record<TipoServizioPrecredit, string> = {
  wink_on_cash: "Wink On Cash",
  evo_cash: "Evo Cash",
  bam: "BAM",
  mondialpol: "Mondialpol",
  fabrik: "Fabrik",
  nessun_collegamento_banca: "Nessuna banca",
  altro: "Altro",
};

export const TIPO_POSSESSO_LABELS: Record<TipoPossessoDevice, string> = {
  venduto_evo: "Venduto da Evo",
  venduto_terzi: "Venduto da terzi",
  noleggio: "Noleggio",
  proprieta_cliente: "Proprietà cliente",
};

export interface CatalogoRidotto {
  id: string;
  marca: string;
  modello: string;
  sigla?: string | null;
}

export interface RelazioneCliente {
  id: string;
  codice_gdo: string | null;
  data_inizio: string | null;
  /** null = relazione in corso. Valorizzata = relazione chiusa, quindi storica. */
  data_fine: string | null;
  tipo_servizio_precredit: TipoServizioPrecredit | null;
  /** Conto tecnico della coppia cassaforte–cliente (issue #211). */
  conto_tecnico: string | null;
  /** Operatori territoriali del punto — solo per le Casseforti. */
  trasporto_valori_id: string | null;
  sala_conta_id: string | null;
  codice_banca_partner: string | null;
  valore_assicurazione: string | null;
  note: string | null;
  cliente: { id: string; nome: string; codice_cliente: string | null };
  sede_cliente: { id: string; nome_sede: string | null; citta: string | null; indirizzo?: string | null } | null;
}

/**
 * Che cosa è un pezzo del parco. Rispecchia l'enum Prisma `TipoDispositivo`.
 * `null` = non classificato, che è lo stato di quasi tutto il parco storico e
 * NON è sinonimo di "cassaforte".
 */
export type TipoDispositivoDevice = "Cassaforte" | "Rendiresto" | "TVCC" | "Allarme" | "Concentratore" | "Altro";

export const TIPO_DISPOSITIVO_LABELS: Record<TipoDispositivoDevice, string> = {
  Cassaforte: "Cassaforte",
  Rendiresto: "Rendiresto",
  TVCC: "TVCC",
  Allarme: "Allarme",
  Concentratore: "Concentratore (accessorio)",
  Altro: "Altro",
};

/** Ordine di comparsa nella select della scheda device. */
export const TIPI_DISPOSITIVO: TipoDispositivoDevice[] = [
  "Cassaforte",
  "Rendiresto",
  "Concentratore",
  "TVCC",
  "Allarme",
  "Altro",
];

export interface Device {
  id: string;
  matricola: string | null;
  /** Modello del catalogo, se la riconciliazione è riuscita. */
  dispositivo_catalogo: CatalogoRidotto | null;
  /** Modello come scritto nella fonte, quando il catalogo non l'ha risolto. */
  modello_raw: string | null;
  stato: StatoDevice;
  /** Nullable: per gran parte del parco storico non è noto. */
  tipo_possesso: TipoPossessoDevice | null;
  /** Nullable per lo stesso motivo: `null` = non noto, diverso da `false`. */
  installato_da_evo: boolean | null;
  /**
   * Che cosa è questo pezzo. `null` = non classificato: quasi tutto il parco
   * storico, e non vuol dire "cassaforte". Gli accessori (concentratori) sono
   * esclusi dalle statistiche per servizio, dove un servizio non l'avranno mai.
   */
  tipo_dispositivo: TipoDispositivoDevice | null;
  /**
   * Il centro che ha montato il device. Presente sia nel dettaglio sia
   * nell'elenco, dove alimenta una colonna spenta di default.
   */
  centro_assistenza_installazione?: { id: string; nome: string } | null;
  data_installazione: string | null;
  data_attivazione: string | null;
  data_dismissione: string | null;
  /** Arrivo presunto in magazzino dal fornitore (data ISO a mezzanotte UTC); `null` = non nota. */
  data_consegna_prevista: string | null;
  /**
   * Quando è stata caricata dal Magazzino; `null` per tutto il parco storico.
   * La scrive solo il backend: vedi `whereCassaforteInMagazzino`.
   */
  caricata_in_magazzino_il: string | null;
  /**
   * Dove sta il pezzo ADESSO: una cassaforte è un oggetto fisico e ha una
   * posizione sola, anche quando più clienti ci versano dentro.
   *
   * Da non confondere con le sedi dentro `clienti`, che sono storicizzate
   * insieme alla relazione e dicono dove stava *mentre serviva quel cliente*.
   */
  sede_id?: string | null;
  sede_cliente?: { id: string; nome_sede: string | null; citta: string | null; indirizzo?: string | null } | null;
  note: string | null;
  created_at: string;
  updated_at: string;
  /** Nell'elenco contiene SOLO le relazioni correnti; nel dettaglio tutte. */
  clienti: RelazioneCliente[];
}

export interface DeviceEvento {
  id: string;
  tipo_evento: string;
  data_evento: string;
  note: string | null;
}

export interface DeviceAssistenza {
  id: string;
  tipo_assistenza: "evo" | "cliente";
  durata_mesi: number | null;
  data_inizio: string | null;
  data_scadenza: string | null;
  fornitore_assistenza_cliente: string | null;
  centro_assistenza: { id: string; nome: string } | null;
}

export interface DeviceNoleggio {
  id: string;
  data_inizio: string | null;
  data_fine: string | null;
  societa_noleggio: { id: string; nome: string };
}

export interface DeviceSostituzione {
  id: string;
  motivo: string;
  data_sostituzione: string | null;
  device_nuovo?: { id: string; matricola: string | null };
  device_vecchio?: { id: string; matricola: string | null };
}

/** Record operativi che referenziano il device — riga cliccabile, non scheda completa. */
export interface InstallazioneDiDevice {
  id: string;
  insegna: string | null;
  localita: string | null;
  ipotesi_data: string | null;
  data_effettiva: string | null;
  stato: string | null;
  /** Il centro che ha eseguito questa installazione, secondo il calendario. */
  centro_assistenza: { id: string; nome: string } | null;
  /** I sopralluoghi da cui questa installazione deriva. */
  sopralluoghi: { sopralluogo: SopralluogoDiDevice }[];
}

/**
 * Un sopralluogo nello storico del device.
 *
 * Ci arriva per DUE strade, e per chi guarda lo storico sono la stessa cosa:
 * attraverso l'installazione (il caso normale — il rilievo precede il pezzo),
 * oppure collegato direttamente al device (`Sopralluogo.device_id`, per i
 * rilievi su una cassaforte gia' installata). Il frontend le unisce in una
 * sezione sola: due elenchi separati direbbero che c'e' una differenza che per
 * chi legge non esiste.
 */
export interface SopralluogoDiDevice {
  id: string;
  insegna_cliente: string | null;
  data_sopralluogo: string;
  stato: string;
}

export interface AttivazioneDiDevice {
  id: string;
  cliente_nome: string | null;
  sede: string | null;
  data_attivazione: string | null;
  data_effettiva: string | null;
  stato: string | null;
  /**
   * 'LAN' | 'SIM' | 'Nessuna' — come è collegata la cassaforte. È un fatto
   * dell'ATTIVAZIONE, non del pezzo: la scheda del dispositivo lo deriva da
   * qui invece di tenerne una copia.
   */
  comunicazione_tipo: string | null;
}

export interface InterventoDiDevice {
  id: string;
  nome: string | null;
  cliente_nome: string | null;
  data_prevista: string | null;
  data_completamento: string | null;
  stato: string | null;
}

export interface DeviceDettaglio extends Device {
  eventi: DeviceEvento[];
  assistenze: DeviceAssistenza[];
  noleggi: DeviceNoleggio[];
  sostituito_da: DeviceSostituzione[];
  sostituisce: DeviceSostituzione[];
  installazioni: InstallazioneDiDevice[];
  attivazioni: AttivazioneDiDevice[];
  interventi: InterventoDiDevice[];
  /** Solo quelli collegati DIRETTAMENTE al pezzo: gli altri stanno dentro `installazioni`. */
  sopralluoghi: SopralluogoDiDevice[];
}

export interface FiltriDevice {
  stato?: StatoDevice | "";
  /**
   * Uno o più clienti. L'array serve al drill-down dalla mappa Wink On Cash,
   * dove il filtro cliente è multiplo: con un solo valore ammesso, aprire
   * l'elenco da lì avrebbe mostrato tutti i clienti invece di quelli scelti.
   */
  cliente_id?: string | string[];
  /** Servizio della relazione corrente, o "non_specificato" per quelle senza. */
  servizio?: TipoServizioPrecredit | "non_specificato" | "";
  /**
   * Tipo di device, o "accessori" per tutti quelli che un servizio non possono
   * averlo. Quale sia l'elenco lo decide il backend, non questa stringa.
   */
  tipo?: TipoDispositivoDevice | "accessori" | "";
  /**
   * Le due cause per cui un device non compare sulla mappa Wink On Cash, come
   * le conta il widget: `senza_sede` (relazione senza sede collegata) e
   * `sede_non_geolocalizzata` (sede collegata ma senza coordinate). I valori
   * sono gli stessi di `motivo_esclusione` lato backend, di proposito.
   */
  mappa?: "senza_sede" | "sede_non_geolocalizzata" | "";
  /** Ricerca libera sulla matricola. */
  q?: string;
  /** Con cliente_id: solo le relazioni ancora aperte. */
  solo_correnti?: boolean;
  /**
   * I device che stanno FISICAMENTE in questa sede — `devices.sede_id`, la
   * posizione dichiarata. Da non confondere con la sede delle relazioni
   * commerciali: su una cassaforte condivisa quelle sono più d'una e dicono
   * dove stava per quel cliente.
   */
  sede_id?: string;
  /**
   * Solo i device CONDIVISI: più di una relazione cliente aperta insieme, cioè
   * una cassaforte in cui versano due insegne diverse. Il conteggio non si può
   * esprimere in un `where` di Prisma, quindi il backend lo risolve con una
   * query a parte — vedi `idDeviceCondivisi` in routes/devices.ts.
   */
  condivisi?: boolean;
}

export async function listDevices(filtri: FiltriDevice = {}): Promise<{ records: Device[] }> {
  const { data } = await apiClient.get<{ records: Device[] }>("/devices", {
    params: {
      stato: filtri.stato || undefined,
      cliente_id: Array.isArray(filtri.cliente_id)
        ? filtri.cliente_id.length > 0
          ? filtri.cliente_id
          : undefined
        : filtri.cliente_id || undefined,
      servizio: filtri.servizio || undefined,
      tipo: filtri.tipo || undefined,
      // Drill-down dalle due righe "non sulla mappa" del widget Wink On Cash.
      mappa: filtri.mappa || undefined,
      q: filtri.q || undefined,
      solo_correnti: filtri.solo_correnti ? "true" : undefined,
      sede_id: filtri.sede_id || undefined,
      condivisi: filtri.condivisi ? "true" : undefined,
    },
    // `cliente_id` può essere un array: senza questa opzione axios lo
    // serializzerebbe come `cliente_id[]=a`, che lato Express diventa una
    // chiave chiamata "cliente_id[]" e il filtro sparirebbe in silenzio.
    // Stessa impostazione di ExportButton, che manda già array da tempo.
    paramsSerializer: { indexes: null },
  });
  return data;
}

export interface StatisticheDevice {
  /** Conteggi sulle relazioni CORRENTI, per servizio. Le chiavi assenti valgono 0. */
  per_servizio: Partial<Record<TipoServizioPrecredit, number>>;
  /** Relazioni correnti senza servizio indicato — voce a sé, non un servizio. */
  servizio_non_specificato: number;
  per_stato: Partial<Record<StatoDevice, number>>;
  /**
   * Device attivi che un servizio Pre Credit non possono averlo — oggi i
   * concentratori. Sono ESCLUSI dai conteggi per servizio qui sopra: contarli
   * fra i "non specificato" li farebbe sembrare lavoro di verifica ancora da
   * fare, mentre per loro quel campo non ha senso.
   */
  accessori: number;
  /**
   * Device con più di una relazione cliente aperta insieme. Senza filtro sullo
   * stato, a differenza dei conteggi per servizio: la condivisione è un fatto
   * delle relazioni aperte, non di un servizio in corso.
   */
  condivisi: number;
}

export async function getStatisticheDevice(): Promise<StatisticheDevice> {
  const { data } = await apiClient.get<StatisticheDevice>("/devices/stats");
  return data;
}

export async function getDevice(id: string): Promise<{ record: DeviceDettaglio }> {
  const { data } = await apiClient.get<{ record: DeviceDettaglio }>(`/devices/${id}`);
  return data;
}

/** Un device Wink On Cash attivo, posizionato sulla sede della relazione corrente. */
export interface PuntoDeviceMappa {
  device_id: string;
  matricola: string | null;
  cliente: string | null;
  cliente_id: string | null;
  sede_nome: string | null;
  sede_citta: string | null;
  lat: number;
  lng: number;
  /**
   * 'indirizzo' = il pin sta dove sta la sede. 'comune' = centro del paese.
   * 'manuale' = posizione inserita a mano dalla scheda Sede, precisa quanto
   * 'indirizzo'. Solo 'comune' si disegna approssimato.
   */
  precisione: GeoPrecisione;
  /**
   * Quanti punti vendita Wink On Cash stanno dietro a questo pin. Quasi sempre
   * 1; è > 1 per le casseforti condivise, dove il pin resta uno solo perché
   * duplicarlo sulla stessa coordinata non aggiungerebbe niente.
   */
  relazioni_servite: number;
}

export interface MappaDevice {
  punti: PuntoDeviceMappa[];
  /**
   * Quante RELAZIONI correnti Wink On Cash sono rappresentate sulla mappa. Non
   * coincide con `punti.length` quando una cassaforte serve più clienti, e la
   * differenza è voluta: qui la domanda è quanti punti vendita usano il
   * servizio, mentre la tessera del riepilogo Dispositivi conta device perché
   * lì il numero promette quante righe si vedranno cliccandola.
   */
  relazioni_disegnate: number;
  /**
   * Le relazioni che rientrano nel filtro ma non hanno dove essere disegnate.
   * Stessa unità dei punti — relazioni, non device — così disegnate + escluse
   * torna al totale, e chi somma i numeri a schermo non trova un buco.
   * Le due cause restano separate perché sono due lavori diversi: collegare una
   * sede è anagrafica, geocodificarla è un'altra cosa. Seguono lo STESSO filtro
   * cliente dei punti.
   */
  esclusi: { senza_sede: number; sede_non_geolocalizzata: number };
}

export async function getMappaDevice(clienteIds: string[] = []): Promise<MappaDevice> {
  const { data } = await apiClient.get<MappaDevice>("/devices/mappa", {
    // Nessun cliente selezionato = nessun filtro, non "nessun risultato".
    params: clienteIds.length > 0 ? { cliente_id: clienteIds } : {},
    // Ripete la chiave invece di unire con virgole: ?cliente_id=a&cliente_id=b,
    // che è la forma che la route si aspetta.
    paramsSerializer: { indexes: null },
  });
  return data;
}

/**
 * Campi modificabili dalla scheda del device.
 *
 * `matricola` si può solo IMPOSTARE quando manca: una volta valorizzata la
 * route rifiuta di cambiarla, perché è l'identificativo fisico a cui punta
 * tutto lo storico. Fuori di proposito i legami
 * cliente/sede, che vivono in `device_cliente` e sono storicizzati — spostare
 * un device è chiudere una relazione e aprirne un'altra, non riscrivere un
 * campo.
 */
/**
 * Il servizio di precredito NON sta sul device: sta sulla relazione
 * device↔cliente, perché è un fatto di quella coppia — la stessa cassaforte
 * può viaggiare con banche diverse per clienti diversi.
 */
export interface DeviceClientePatch {
  tipo_servizio_precredit?: TipoServizioPrecredit | null;
  /** Operatori territoriali del punto — solo per le Casseforti. */
  trasporto_valori_id?: string | null;
  sala_conta_id?: string | null;
  /** Sede presso cui il device è collocato per QUESTA relazione. */
  sede_id?: string | null;
  /** Codice GDO valido per questa relazione, non per il device in assoluto. */
  codice_gdo?: string | null;
  /** Conto tecnico di questa coppia cassaforte–cliente. */
  conto_tecnico?: string | null;
  /**
   * Chiude la relazione: da qui in poi è storia, non "di chi è oggi".
   *
   * È l'UNICO modo previsto per staccare un cliente da una cassaforte senza
   * perdere il passato — la riga resta, con la sede, il codice GDO e il
   * servizio che valevano in quel periodo. Rimetterla a `null` la
   * riaprirebbe, ma non è un'operazione che l'interfaccia offre: un rapporto
   * che ricomincia è una riga NUOVA, non un campo da svuotare, altrimenti il
   * periodo di stacco sparisce come se non fosse mai esistito.
   *
   * Il backend accetta il campo da sempre (`DATE_FIELDS_DEVICE_CLIENTE` in
   * routes/devices.ts lo converte, e la PATCH scrive `device_cliente.chiudi`
   * nell'audit log): mancava solo qui, quindi dall'app non si poteva chiudere
   * niente.
   */
  data_fine?: string | null;
}

export async function updateDeviceCliente(id: string, payload: DeviceClientePatch): Promise<unknown> {
  const { data } = await apiClient.patch(`/device-cliente/${id}`, payload);
  return data;
}

/**
 * Collega il device a un cliente IN PIÙ, aprendo una nuova relazione.
 *
 * Non sostituisce quelle esistenti: una cassaforte può servire più punti
 * vendita insieme. Spostarla altrove è un'altra operazione — lì si chiude la
 * relazione vecchia e se ne apre una nuova, non si riscrive quella che c'è.
 *
 * Il backend rifiuta con 409 se una relazione ANCORA APERTA con lo stesso
 * cliente esiste già: sarebbe un doppione, e renderebbe ambiguo "di chi è
 * questo device oggi".
 */
export interface NuovaRelazioneCliente {
  device_id: string;
  cliente_id: string;
  sede_id?: string | null;
  codice_gdo?: string | null;
  tipo_servizio_precredit?: TipoServizioPrecredit | null;
  conto_tecnico?: string | null;
  trasporto_valori_id?: string | null;
  sala_conta_id?: string | null;
}

export async function creaDeviceCliente(payload: NuovaRelazioneCliente): Promise<unknown> {
  const { data } = await apiClient.post("/device-cliente", payload);
  return data;
}

/**
 * Cancella FISICAMENTE una riga di `device_cliente`.
 *
 * Non è il modo di far finire un rapporto: per quello c'è `data_fine`
 * (vedi `DeviceClientePatch`), che chiude la relazione lasciandola leggibile.
 * Questa serve all'altro caso, quello in cui la riga non doveva esistere —
 * il cliente sbagliato scelto nell'autocomplete, un doppione dell'import.
 *
 * Irreversibile e senza rete di sicurezza: nessuna tabella ha una FK verso
 * `device_cliente`, quindi il database la lascia sparire senza obiettare e il
 * backend non ha un 409 con l'elenco dei collegati come la DELETE del device.
 * Sparisce anche il codice GDO e il servizio di quel periodo, che da nessuna
 * altra parte sono scritti.
 *
 * Solo admin (`devices:delete`).
 */
export async function deleteDeviceCliente(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/device-cliente/${id}`);
  return data;
}

export interface DevicePatch {
  stato?: StatoDevice;
  tipo_possesso?: TipoPossessoDevice | null;
  tipo_dispositivo?: TipoDispositivoDevice | null;
  installato_da_evo?: boolean | null;
  centro_assistenza_installazione_id?: string | null;
  data_installazione?: string | null;
  data_attivazione?: string | null;
  data_dismissione?: string | null;
  dispositivo_catalogo_id?: string | null;
  /**
   * Posizione fisica attuale del device. Da non confondere con il `sede_id` di
   * `DeviceClientePatch`, che è la sede di UNA relazione commerciale: quello
   * dice dove stava il device per quel cliente, questo dove sta il pezzo.
   */
  sede_id?: string | null;
  data_consegna_prevista?: string | null;
  /** Solo se il device non ne ha ancora una: vedi la nota sopra `DeviceClientePatch`. */
  matricola?: string;
  note?: string | null;
}

export async function updateDevice(id: string, payload: DevicePatch): Promise<{ record: Device }> {
  const { data } = await apiClient.patch<{ record: Device }>(`/devices/${id}`, payload);
  return data;
}

/**
 * Censimento di un pezzo fisico nuovo.
 *
 * La matricola è l'unico campo obbligatorio, e non è una svista di questo tipo:
 * la rotta rifiuta solo quella. Tutto il resto può mancare — vedi le note sullo
 * schema — perché un device censito senza modello vale più di un device non
 * censito, e un possesso inventato per riempire il campo non si distinguerebbe
 * più da uno verificato.
 *
 * `cliente_id`/`sede_relazione_id` non appartengono al device: aprono la
 * relazione device↔cliente, che è ciò che fa comparire il pezzo sulla scheda
 * della sede (quel contatore legge `device_cliente`, non `devices.sede_id`).
 */
export interface NuovoDevice {
  matricola: string;
  dispositivo_catalogo_id?: string | null;
  modello_raw?: string | null;
  tipo_dispositivo?: TipoDispositivoDevice | null;
  tipo_possesso?: TipoPossessoDevice | null;
  installato_da_evo?: boolean | null;
  centro_assistenza_installazione_id?: string | null;
  sede_id?: string | null;
  data_installazione?: string | null;
  data_attivazione?: string | null;
  note?: string | null;
}

export async function createDevice(payload: NuovoDevice): Promise<{ record: Device }> {
  const { data } = await apiClient.post<{ record: Device }>("/devices", payload);
  return data;
}

/**
 * "Questa matricola è già censita?" — la domanda che i form Installazione e
 * Attivazione fanno mentre si compila, per sapere se stanno per salvare un
 * record che punta a un pezzo del parco o a niente.
 *
 * `matricola` torna NORMALIZZATA dal backend (trim + maiuscolo): è la stessa
 * forma con cui verrà salvata, e mostrarla all'utente è il modo di dirgli che
 * " ub120004" e "UB120004" sono lo stesso seriale.
 */
export interface EsitoMatricola {
  trovato: boolean;
  matricola: string;
  device_id: string | null;
}

export async function cercaPerMatricola(matricola: string): Promise<EsitoMatricola> {
  const { data } = await apiClient.get<EsitoMatricola>(`/devices/per-matricola/${encodeURIComponent(matricola)}`);
  return data;
}

/**
 * Quanti record collegati impediscono l'eliminazione di un device. Arriva nel
 * corpo del 409: dire QUALI e QUANTI è la differenza fra "non si può" e sapere
 * che cosa staccare per procedere.
 */
export interface DeviceCollegati {
  clienti: number;
  eventi: number;
  assistenze: number;
  noleggi: number;
  installazioni: number;
  attivazioni: number;
  interventi: number;
  sostituzioni: number;
  /** Fase 4 punto 8: FK in RESTRICT da `magazzino_device_unita`. */
  magazzino: number;
}

/**
 * Etichette leggibili per il messaggio di blocco: le chiavi grezze sono nomi di
 * tabella. Singolare e plurale perché il conteggio spesso vale 1, e "ha 1
 * relazioni con clienti" è scritto male in un messaggio che l'utente legge
 * proprio quando è già contrariato.
 */
export const COLLEGATI_LABELS: Record<keyof DeviceCollegati, [singolare: string, plurale: string]> = {
  clienti: ["relazione con un cliente", "relazioni con clienti"],
  eventi: ["evento", "eventi"],
  assistenze: ["contratto di assistenza", "contratti di assistenza"],
  noleggi: ["noleggio", "noleggi"],
  installazioni: ["installazione", "installazioni"],
  attivazioni: ["attivazione", "attivazioni"],
  interventi: ["intervento", "interventi"],
  sostituzioni: ["sostituzione", "sostituzioni"],
  magazzino: ["unità collegata a un ordine di magazzino", "unità collegate a ordini di magazzino"],
};

/** "1 intervento" / "3 interventi", per il messaggio di blocco. */
export function etichettaCollegati(chiave: keyof DeviceCollegati, quanti: number): string {
  const [singolare, plurale] = COLLEGATI_LABELS[chiave];
  return `${quanti} ${quanti === 1 ? singolare : plurale}`;
}

/**
 * Eliminazione definitiva. Il backend rifiuta con 409 se c'è storico collegato,
 * e in quel caso il corpo porta `dettaglio` con i conteggi per tipo: un device
 * non più in uso va impostato come dismesso, non cancellato.
 */
export async function deleteDevice(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/devices/${id}`);
  return data;
}

/**
 * Il modello da mostrare: il catalogo se c'è, altrimenti la stringa grezza
 * della fonte. Sta qui e non nelle pagine perché la stessa scelta serve
 * nell'elenco, nel dettaglio e nella card della Vista Sede: tre copie
 * divergerebbero alla prima modifica.
 */
export function modelloLeggibile(d: {
  dispositivo_catalogo: CatalogoRidotto | null;
  modello_raw: string | null;
}): string | null {
  if (d.dispositivo_catalogo) {
    return `${d.dispositivo_catalogo.marca} ${d.dispositivo_catalogo.modello}`;
  }
  return d.modello_raw;
}

/**
 * Le relazioni cliente in corso. Sono PIÙ DI UNA per i device condivisi — la
 * stessa cassaforte su cui versano due aziende diverse — che è il caso per cui
 * device_cliente è una tabella ponte invece di una colonna. Chi mostra solo la
 * prima nasconde metà del dato proprio dove conta.
 */
export function relazioniCorrenti(d: { clienti: RelazioneCliente[] }): RelazioneCliente[] {
  return d.clienti.filter((c) => c.data_fine === null);
}

/**
 * Il centro che ha installato il device, con la fonte da cui lo sappiamo.
 *
 * Dove esiste un'installazione collegata nel calendario, quella vince: è un
 * record dell'installazione vera, mentre la colonna sul device è un dato
 * inserito a mano (o mai inserito, per il parco storico importato).
 */
export function centroInstallazione(d: {
  centro_assistenza_installazione?: { id: string; nome: string } | null;
  installazioni?: { centro_assistenza: { id: string; nome: string } | null }[];
}): { nome: string; daInstallazione: boolean } | null {
  const daSchedule = d.installazioni?.find((i) => i.centro_assistenza)?.centro_assistenza;
  if (daSchedule) return { nome: daSchedule.nome, daInstallazione: true };
  if (d.centro_assistenza_installazione) {
    return { nome: d.centro_assistenza_installazione.nome, daInstallazione: false };
  }
  return null;
}

/** La prima relazione in corso, per i punti dove ne serve una sola. */
export function relazioneCorrente(d: { clienti: RelazioneCliente[] }): RelazioneCliente | null {
  return relazioniCorrenti(d)[0] ?? null;
}
